import { addDataToQueryString } from '@/helpers/strings'
import api from './api'
import { CreateRes, PaginatedData, PaginationReq } from './api.types'
import { SURVEY_RESPONSES, SURVEY_THEMES, SURVEYS } from './endpoints'

export interface SurveyListResDTO {
  id: number
  title: string
  description: string
  banner: string
  status: SurveyStatus
  created_at: string
  updated_at: string
  response_count: string
  awaiting_response_count: number
  alumni_status?: AlumnusStatus

  latest_score: string
  last_assessment: string
  current_assessment: string
  current_assessment_id: number
  last_assessment_id: string
  times_taken: string
  progress_percentage: number
  has_awaiting_review: boolean
  can_update: boolean
}

export interface ResponsesListResDTO {
  id: number
  survey_id: number
  survey_title: string
  respondent_name: string
  submitted_at: string
  score?: number
  max_possible_score?: number
  score_percentage?: number
  progress_percentage?: number
  status: AlumnusStatus
  answers: Answer[]
  is_published: boolean
}

export interface ResponseDetailResDTO
  extends Omit<ResponsesListResDTO, 'answers'> {
  answers: AnswerDetail[]
  is_complete?: boolean
}
interface AnswerDetail {
  id: number
  question_id: number
  question_text: string
  question_type: QuestionType
  text_answer: string
  selected_option_texts: string[]
  selected_options: number[]
  max_score: string
  score: number
  linear_value: string
  created_at: string
}

export interface Theme {
  category: ThemeCategory
  color: string
  id?: number
}

export interface TopPerformers {
  alumni_id: number
  name: string
  cohort: string
  score: string
}
export interface SurveyScoreDistribution {
  percentiles: Percentile[]
}

interface Percentile {
  percentile: string
  count: number
}
export interface RespondentListResDTO {
  name: string
  cohort: string
  last_submission: string
  total_submissions: number
  highest_score: string
  respondent_id: number
  last_submission_status: SubmissionStatus
}
export interface RespondToSurveyReqDTO {
  answers: Answer[]
  is_published?: boolean
}

interface Answer {
  id?: number
  question_id: number
  selected_options: number[]
  text_answer?: string
  linear_value?: number
  score?: number
}
export interface SurveyDetail {
  id: number
  title: string
  description: string
  status: string
  banner: string
  custom_theme: {
    background: string
    color: string
  }
  sections: Section[]
  progress_percentage: number
  has_awaiting_review: boolean
  can_update: boolean
  updated_at?: string
}

interface Section {
  id?: number
  title: string
  description: string
  media?: string
  order: number
  questions: Question[]
}
type QuestionType =
  | 'short_answer'
  | 'multiple_choice'
  | 'checkboxes'
  | 'linear_scale'
interface Question {
  id?: number
  question_type: QuestionType

  text: string
  description: string
  order: number
  media1?: string
  media2?: string
  media3?: string
  max_score?: number
  score?: number
  score_per_answer?: number
  options?: Option[]
  linear_scale_config?: Linearscaleconfig
}

interface Linearscaleconfig {
  min_value: number
  max_value: number
  min_label: string
  max_label: string
  step?: number
}

interface Option {
  id: number
  text: string
  value: number
  is_correct: boolean
  order: number
}

export enum AlumnusStatus {
  IN_PROGRESS = 'in_progress',
  COMPLETED = 'completed',
  AWAITING = 'awaiting',
  GRADED = 'graded',
}

export enum DateRange {
  LAST_30_DAYS = '30_days',
  LAST_60_DAYS = '60_days',
  LAST_1_YEAR = '1_year',
}

export enum SurveyStatus {
  DRAFT = 'draft',
  PUBLISHED = 'published',
  UNPUBLISHED = 'unpublished',
}
export enum SubmissionStatus {
  PENDING = 'pending',
  SCORED = 'scored',
}

export enum ThemeCategory {
  COLOR = 'base',
  BACKGROUND = 'background',
}

export type FetchSurveyReqDTO = PaginationReq<{
  alumnus_id?: number
  alumni_status?: AlumnusStatus
  date_range?: DateRange
}>

export const getSurveys = async (payload: FetchSurveyReqDTO) => {
  const qs = addDataToQueryString('', payload)
  return (
    await api.get<{ data: PaginatedData<SurveyListResDTO> }>(
      SURVEYS + (qs ? '?' + qs : '')
    )
  ).data
}

export const getSurveyDetails = async (id: number) => {
  return (await api.get<{ data: SurveyDetail }>(SURVEYS + id + '/')).data
}

export const getSurveyScoreDistribution = async (id: number) => {
  return (
    await api.get<{ data: SurveyScoreDistribution }>(
      SURVEYS + id + '/score-distribution/'
    )
  ).data
}
export const getSurveyTopPerformers = async (id: number) => {
  return (
    await api.get<{ data: PaginatedData<TopPerformers> }>(
      SURVEYS + id + '/top-performances/?page=1&page_size=40'
    )
  ).data
}

export const getSurveyRespondents = async (
  payload: PaginationReq<{
    last_submission_status?: SubmissionStatus
    id: number
  }>
) => {
  const { id, ...rest } = payload
  const qs = addDataToQueryString('', rest)
  return (
    await api.get<{ data: PaginatedData<RespondentListResDTO> }>(
      SURVEYS + id + '/respondents/' + (qs ? '?' + qs : '')
    )
  ).data
}

export const createSurvey = async (payload: FormData) => {
  return await api.post<{ data: SurveyListResDTO }>(SURVEYS, payload)
}

export const deleteSurvey = async (id: number) => {
  return (await api.delete<{ data: any }>(SURVEYS + id + '/')).data
}

export const updateSurvey = async (payload: FormData) => {
  const id = payload.get('id')
  payload.delete('id')
  return await api.put<{ data: SurveyDetail }>(SURVEYS + id + '/', payload)
}

export const duplicateSurvey = async (id: number) => {
  return await api.post<{ data: SurveyListResDTO }>(
    `${SURVEYS}${id}/duplicate/`,
    {}
  )
}

export const changeSurveyStatus = async (id: number) => {
  return await api.post<{ data: SurveyDetail }>(`${SURVEYS}${id}/publish/`, {})
}

export const createTheme = async (payload: Theme) => {
  return await api.post<{ data: CreateRes<Theme> }>(SURVEY_THEMES, payload)
}
export const deleteTheme = async (id: number) => {
  return await api.delete<{ data: any }>(SURVEY_THEMES + id + '/')
}

export const getThemes = async (payload: PaginationReq) => {
  const qs = addDataToQueryString('', payload)
  return (
    await api.get<{ data: PaginatedData<CreateRes<Theme>> }>(
      SURVEY_THEMES + (qs ? '?' + qs : '')
    )
  ).data
}

export const updateResponse = async (
  payload: RespondToSurveyReqDTO & { id: number }
) => {
  const { id, ...rest } = payload
  return await api.patch<{ data: ResponsesListResDTO }>(
    `${SURVEY_RESPONSES}${id}/`,
    rest
  )
}
export const deleteResponse = async (id: number) => {
  return await api.delete<{ data: any }>(`${SURVEY_RESPONSES}${id}/`)
}
export const createResponse = async (
  payload: RespondToSurveyReqDTO & { surveyId: number }
) => {
  const { surveyId, ...rest } = payload
  return await api.post<{ data: ResponsesListResDTO }>(
    `${SURVEYS}${surveyId}/respond/`,
    rest
  )
}

export const getResponses = async (
  payload: PaginationReq<{ survey?: number; respondent?: number }>
) => {
  const qs = addDataToQueryString('', payload)
  return (
    await api.get<{ data: PaginatedData<ResponsesListResDTO> }>(
      SURVEY_RESPONSES + (qs ? '?' + qs : '')
    )
  ).data
}

export const getResponseDetails = async (id: number) => {
  return (
    await api.get<{ data: ResponseDetailResDTO }>(SURVEY_RESPONSES + id + '/')
  ).data
}
export const downloadResponsePdf = async (id: number) => {
  return (
    await api.get<{ data: any }>(SURVEY_RESPONSES + id + '/download-pdf/')
  ).data
}
